import { type FormEvent, useState } from 'react';
import { Alert, Button, Field, Select, Textarea } from '../../components/ui';
import { t } from '../../i18n';
import { errorMessage } from '../../lib/errors';
import { SUPPLIER_FIELDS, type SupplierValues } from './data';

const EMPTY: SupplierValues = {
  tradeName: '',
  legalName: '',
  taxId: '',
  country: 'PA',
  address: null,
  contactName: null,
  email: null,
  phone: null,
  bankName: null,
  bankAccount: null,
  bankAccountType: null,
  bankAccountHolder: null,
  notes: null,
};

/** Alta o edición de un proveedor. Al editar se envían solo los campos que cambiaron (la auditoría guarda cada uno). */
export function SupplierForm({ initial, submitLabel, onSubmit, onCancel }: { initial?: SupplierValues; submitLabel: string; onSubmit: (values: Partial<SupplierValues>) => Promise<void>; onCancel: () => void }) {
  const [v, setV] = useState<SupplierValues>(initial ?? EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof SupplierValues>(k: K) => (e: { target: { value: string } }) => setV((prev) => ({ ...prev, [k]: e.target.value }));
  const text = (k: keyof SupplierValues) => (v[k] as string | null) ?? '';

  async function submit(e: FormEvent) {
    e.preventDefault();
    const clean = Object.fromEntries(SUPPLIER_FIELDS.map((k) => [k, typeof v[k] === 'string' ? (v[k] as string).trim() || null : v[k]])) as unknown as SupplierValues;
    const body = initial ? Object.fromEntries(SUPPLIER_FIELDS.filter((k) => (initial[k] ?? null) !== clean[k]).map((k) => [k, clean[k]])) : clean;
    if (initial && Object.keys(body).length === 0) return onCancel();
    setBusy(true);
    setError(null);
    try {
      await onSubmit(body);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      {error && <Alert>{error}</Alert>}
      <fieldset className="space-y-4">
        <legend className="text-sm font-semibold text-slate-900">{t('pv.generales')}</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="pv-comercial" label={t('pv.campo_tradeName')} required minLength={2} maxLength={200} value={v.tradeName} onChange={set('tradeName')} />
          <Field id="pv-razon" label={t('pv.campo_legalName')} required minLength={2} maxLength={200} value={v.legalName} onChange={set('legalName')} />
          <Field id="pv-ruc" label={t('pv.campo_taxId')} required minLength={3} maxLength={40} value={v.taxId} onChange={set('taxId')} />
          <Field id="pv-pais" label={t('pv.campo_country')} maxLength={2} pattern="[A-Za-z]{2}" placeholder="PA" value={text('country')} onChange={set('country')} />
        </div>
        <Field id="pv-direccion" label={t('pv.campo_address')} maxLength={500} value={text('address')} onChange={set('address')} />
      </fieldset>
      <fieldset className="space-y-4">
        <legend className="text-sm font-semibold text-slate-900">{t('pv.contacto')}</legend>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field id="pv-contacto" label={t('pv.campo_contactName')} maxLength={200} value={text('contactName')} onChange={set('contactName')} />
          <Field id="pv-correo" type="email" label={t('pv.campo_email')} maxLength={254} value={text('email')} onChange={set('email')} />
          <Field id="pv-telefono" label={t('pv.campo_phone')} maxLength={50} value={text('phone')} onChange={set('phone')} />
        </div>
      </fieldset>
      <fieldset className="space-y-4">
        <legend className="text-sm font-semibold text-slate-900">{t('pv.bancarios')}</legend>
        <p className="text-xs text-slate-500">{t('pv.bancarios_ayuda')}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="pv-banco" label={t('pv.campo_bankName')} maxLength={120} value={text('bankName')} onChange={set('bankName')} />
          <Field id="pv-cuenta" label={t('pv.campo_bankAccount')} maxLength={60} value={text('bankAccount')} onChange={set('bankAccount')} />
          <Select id="pv-tipo" label={t('pv.campo_bankAccountType')} value={text('bankAccountType')} onChange={(e) => setV((prev) => ({ ...prev, bankAccountType: (e.target.value || null) as SupplierValues['bankAccountType'] }))}>
            <option value="">{t('us.sin_elegir')}</option>
            <option value="corriente">{t('pv.cuenta_corriente')}</option>
            <option value="ahorros">{t('pv.cuenta_ahorros')}</option>
          </Select>
          <Field id="pv-titular" label={t('pv.campo_bankAccountHolder')} maxLength={200} placeholder={t('pv.titular_ayuda')} value={text('bankAccountHolder')} onChange={set('bankAccountHolder')} />
        </div>
      </fieldset>
      <Textarea id="pv-notas" label={t('pv.campo_notes')} rows={3} maxLength={2_000} value={text('notes')} onChange={set('notes')} />
      <div className="flex gap-2">
        <Button type="submit" disabled={busy || v.tradeName.trim().length < 2 || v.legalName.trim().length < 2 || v.taxId.trim().length < 3}>
          {submitLabel}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          {t('comun.cancelar')}
        </Button>
      </div>
    </form>
  );
}
